import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileClock,
  FileJson,
  Globe,
  Image as ImageIcon,
  Images,
  Info,
  Loader2,
  Table2,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { JsonView, darkStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { Modal } from "../../../ui/components/Modal";
import { Button } from "../../../ui/components/Button";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { CollapsibleSection } from "../../../ui/components/CollapsibleSection";
import { ListView } from "../../../ui/components/ListView";
import { ListViewCard } from "../../../ui/components/ListView/components/ListViewCard";
import { TabSwitcher } from "../../../ui/components/TabSwitcher";
import {
  MediaUploader,
  type MediaEntry,
} from "../../../ui/components/MediaUploader";
import { renderMediaTypeBadge } from "./mediaTypeBadge";
import { ImageLoader } from "../../../ui/components/ImageLoader";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { vars } from "../../../ui/styles/theme.css";
import {
  downloadDataJob,
  startExport,
  waitForJob,
} from "../../lib/api/data-transfer";
import type { DataTransferEntity } from "../../lib/api/data-transfer.types";
import {
  fetchCertificationModuleAdmin,
  type CertificationModuleDetail,
} from "../../lib/api/certifications";
import {
  fetchAdminLanguages,
  fetchLocalizationModule,
  fetchModulesForLanguage,
} from "../../lib/api/localization";
import type { Language } from "../../lib/api/localization";
import type { ExportLocalizationSidecar } from "../../lib/api/data-transfer.types";
import { isYoutubeUrl } from "../../lib/media/youtube";
import { getAuthCookie } from "../../lib/auth/session-cookie";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";

type CatalogExportPreview = {
  schema?: string;
  version?: number;
  entity?: string;
  exportedAt?: string;
  enumGroups?: unknown[];
  items?: Array<{
    slug?: string | null;
    name?: string;
    keys?: unknown[];
    alerts?: unknown[];
    [key: string]: unknown;
  }>;
};
const CONTENT_KEY_PREFIX = "certification.content.";
const LANGUAGE_CONTEXTS = ["shared", "pro", "certification", "account"] as const;

type LanguageExportPreview = {
  code: string;
  name: string;
  description: string | null;
  enabled: boolean;
  isDefault: boolean;
  flagUrl: string | null;
  flagMediaKey: string | null;
  modules: Array<{ context: string; definitions: Record<string, string> }>;
};

function isContentLocKey(value: string | null | undefined): value is string {
  return typeof value === "string" && value.startsWith(CONTENT_KEY_PREFIX);
}

/** Collect portable content keys from selected module details (for localization preview). */
function collectContentKeysFromModules(
  modules: CertificationModuleDetail[],
): string[] {
  const keys = new Set<string>();
  const add = (value: string | null | undefined) => {
    if (isContentLocKey(value)) keys.add(value);
  };
  for (const mod of modules) {
    add(mod.title);
    add(mod.description);
    for (const t of mod.trainings ?? []) {
      add(t.title);
      add(t.section);
      add(t.transcript);
      for (const q of t.questions ?? []) {
        add(q.text);
        for (const choice of q.choices ?? []) add(choice);
      }
    }
  }
  return [...keys];
}

function sliceLocalizationSidecar(
  keys: string[],
  defaultLanguage: string,
  defsByLang: Record<string, Record<string, string>>,
): ExportLocalizationSidecar | null {
  if (!keys.length) return null;
  const definitionsByLang: Record<string, Record<string, string>> = {};
  for (const [lang, defs] of Object.entries(defsByLang)) {
    const sliced: Record<string, string> = {};
    for (const key of keys) {
      const value = defs[key];
      if (value != null && value !== "") sliced[key] = value;
    }
    if (Object.keys(sliced).length > 0) {
      definitionsByLang[lang] = sliced;
    }
  }
  if (Object.keys(definitionsByLang).length === 0) {
    return {
      module: "certification",
      defaultLanguage,
      definitionsByLang: { [defaultLanguage]: {} },
    };
  }
  if (!definitionsByLang[defaultLanguage]) {
    definitionsByLang[defaultLanguage] = {};
  }
  return {
    module: "certification",
    defaultLanguage,
    definitionsByLang,
  };
}
export type ExportPreviewItem = {
  id: string;
  label: string;
  matchKey?: string;
  /** Optional thumbnail (e.g. module hero image). */
  imageUrl?: string | null;
};

export interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  entity: DataTransferEntity;
  selectedIds: string[];
  totalCount: number;
  entityLabel: string;
  /** Rows available for the export preview table (best-effort for paginated lists). */
  previewItems?: ExportPreviewItem[];
  defaultFormat?: "csv" | "json" | "zip";
  search?: string;
}

type ExportPhase =
  | "idle"
  | "starting"
  | "preparing"
  | "downloading"
  | "done"
  | "error";

type ProgressCopy = {
  title: string;
  subtitle?: string;
  Icon: LucideIcon;
  iconColor: string;
  spin?: boolean;
};

type CsvPreviewRow = {
  id: string;
  slug?: string;
  title?: string;
  description?: string | null;
  sortOrder?: number;
  isActive?: boolean;
  trainingCount?: number;
  label?: string;
  matchKey?: string;
};

const jsonPreviewShellStyle: React.CSSProperties = {
  borderRadius: 8,
  padding: "12px 16px",
  fontSize: 12,
  lineHeight: 1.6,
  overflowX: "auto",
  fontFamily: "monospace",
  maxHeight: 280,
  overflowY: "auto",
  background: vars.color.muted,
  border: `1px solid ${vars.color.border}`,
};

const thumbCellStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 6,
  overflow: "hidden",
  background: vars.color.muted,
  border: `1px solid ${vars.color.border}`,
  flexShrink: 0,
  position: "relative",
};

const thumbPlaceholderStyle: React.CSSProperties = {
  ...thumbCellStyle,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const previewColumns: ColumnDefinition<ExportPreviewItem>[] = [
  {
    id: "image",
    label: "",
    width: 56,
    enableHiding: false,
    pinnedLeft: true,
    render: (row) =>
      row.imageUrl ? (
        <div style={thumbCellStyle}>
          <ImageLoader src={row.imageUrl} />
        </div>
      ) : (
        <div style={thumbPlaceholderStyle}>
          <ImageIcon size={16} style={{ opacity: 0.4, color: vars.color.fgMuted }} />
        </div>
      ),
  },
  {
    id: "label",
    label: "Item",
    sortable: true,
    render: (row) => row.label,
  },
  {
    id: "matchKey",
    label: "Key",
    sortable: true,
    render: (row) => row.matchKey || "—",
  },
];

const certCsvColumns: ColumnDefinition<CsvPreviewRow>[] = [
  { id: "slug", label: "slug", sortable: true, render: (r) => r.slug ?? "—" },
  { id: "title", label: "title", sortable: true, render: (r) => r.title ?? "—" },
  {
    id: "description",
    label: "description",
    render: (r) => r.description || "—",
  },
  {
    id: "sortOrder",
    label: "sortOrder",
    sortable: true,
    render: (r) => String(r.sortOrder ?? ""),
  },
  {
    id: "isActive",
    label: "isActive",
    sortable: true,
    render: (r) => String(Boolean(r.isActive)),
  },
  {
    id: "trainingCount",
    label: "trainingCount",
    sortable: true,
    render: (r) => String(r.trainingCount ?? 0),
  },
];

const genericCsvColumns: ColumnDefinition<CsvPreviewRow>[] = [
  { id: "id", label: "id", render: (r) => r.id },
  { id: "label", label: "label", sortable: true, render: (r) => r.label ?? "—" },
  {
    id: "matchKey",
    label: "key",
    sortable: true,
    render: (r) => r.matchKey ?? "—",
  },
];

/** Collect media tiles that match what ZIP export embeds (heroes, thumbs, hosted videos). */
function mediaEntriesFromModules(
  modules: CertificationModuleDetail[],
): MediaEntry[] {
  const seen = new Set<string>();
  const entries: MediaEntry[] = [];
  const push = (id: string, url: string, previewUrl?: string | null) => {
    if (seen.has(url)) return;
    seen.add(url);
    entries.push({
      id,
      url,
      ...(previewUrl ? { previewUrl } : {}),
    });
  };

  for (const mod of modules) {
    if (mod.heroImageUrl) {
      push(`hero-${mod.id}`, mod.heroImageUrl, mod.heroImageUrl);
    }
    for (const t of mod.trainings ?? []) {
      if (t.videoUrl && isYoutubeUrl(t.videoUrl)) {
        push(`video-${t.id}`, t.videoUrl, t.thumbnailUrl);
        continue;
      }
      if (t.thumbnailUrl) {
        push(`thumb-${t.id}`, t.thumbnailUrl, t.thumbnailUrl);
      }
      if (t.videoUrl) {
        push(`video-${t.id}`, t.videoUrl, t.thumbnailUrl);
      }
    }
  }
  return entries;
}

function mediaEntriesFromLanguagePacks(
  packs: LanguageExportPreview[],
): MediaEntry[] {
  const seen = new Set<string>();
  const entries: MediaEntry[] = [];
  for (const pack of packs) {
    const url = pack.flagUrl;
    if (!url || seen.has(url)) continue;
    seen.add(url);
    entries.push({
      id: `flag-${pack.code}`,
      url,
      previewUrl: url,
    });
  }
  return entries;
}

function countLanguageDefinitions(packs: LanguageExportPreview[]): number {
  return packs.reduce(
    (total, pack) =>
      total +
      pack.modules.reduce(
        (n, mod) => n + Object.keys(mod.definitions ?? {}).length,
        0,
      ),
    0,
  );
}

function modulesToExportItems(modules: CertificationModuleDetail[]) {
  return modules.map((mod) => ({
    slug: mod.slug,
    title: mod.title,
    description: mod.description,
    sortOrder: mod.sortOrder,
    isActive: mod.isActive,
    heroImageUrl: mod.heroImageUrl,
    trainings: [...(mod.trainings ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.stepNumber - b.stepNumber)
      .map((t) => ({
        stepNumber: t.stepNumber,
        section: t.section,
        title: t.title,
        transcript: t.transcript,
        sortOrder: t.sortOrder,
        videoUrl: t.videoUrl,
        thumbnailUrl: t.thumbnailUrl ?? null,
        questions: [...(t.questions ?? [])]
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((q) => ({
            text: q.text,
            choices: q.choices,
            correctChoiceIndex: q.correctChoiceIndex,
            sortOrder: q.sortOrder,
          })),
      })),
  }));
}

function buildExportEnvelopePreview(
  entity: DataTransferEntity,
  items: unknown[],
  localization?: ExportLocalizationSidecar | null,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  return {
    schema: "lion-pro.export",
    version: 1,
    entity,
    exportedAt: new Date().toISOString(),
    ...(extra ?? {}),
    items,
    ...(localization ? { localization } : null),
  };
}

function progressCopy(phase: ExportPhase, errorMessage: string | null): ProgressCopy | null {
  switch (phase) {
    case "starting":
      return {
        title: "Starting export…",
        subtitle: "Creating an export job.",
        Icon: Loader2,
        iconColor: vars.color.info,
        spin: true,
      };
    case "preparing":
      return {
        title: "Preparing your file…",
        subtitle: "This may take a moment. You can close this dialog.",
        Icon: FileClock,
        iconColor: vars.color.info,
      };
    case "downloading":
      return {
        title: "Downloading…",
        subtitle: "Your file is ready and the download should begin shortly.",
        Icon: Download,
        iconColor: vars.color.info,
      };
    case "done":
      return {
        title: "Download complete",
        subtitle: "Check your browser’s downloads if you don’t see the file.",
        Icon: CheckCircle2,
        iconColor: vars.color.success,
      };
    case "error":
      return {
        title: "Export failed",
        subtitle: errorMessage || "Something went wrong. Please try again.",
        Icon: AlertCircle,
        iconColor: vars.color.danger,
      };
    default:
      return null;
  }
}

export function ExportModal({
  open,
  onClose,
  entity,
  selectedIds,
  totalCount,
  entityLabel,
  previewItems = [],
  defaultFormat,
  search,
}: ExportModalProps) {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? getAuthCookie("access_token") ?? "";
  const hasSelection = selectedIds.length > 0;
  const zipAllowed =
    entity === "certification-modules" || entity === "languages";
  const jsonOnly = entity === "data-catalogs";
  const certZipFeatures = entity === "certification-modules";
  const preferredDefault =
    defaultFormat ?? (zipAllowed ? "zip" : jsonOnly ? "json" : "csv");
  const [scope, setScope] = React.useState<"selection" | "all">(
    hasSelection ? "selection" : "all",
  );
  const [format, setFormat] = React.useState<"csv" | "json" | "zip">(preferredDefault);
  const [phase, setPhase] = React.useState<ExportPhase>("idle");
  const [error, setError] = React.useState<string | null>(null);
  const [jobId, setJobId] = React.useState<string | null>(null);
  const [moduleDetails, setModuleDetails] = React.useState<
    CertificationModuleDetail[]
  >([]);
  const [languagePacks, setLanguagePacks] = React.useState<LanguageExportPreview[]>(
    [],
  );
  const [catalogExportPreview, setCatalogExportPreview] =
    React.useState<CatalogExportPreview | null>(null);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const [certLocByLang, setCertLocByLang] = React.useState<
    Record<string, Record<string, string>>
  >({});
  const [defaultLocLanguage, setDefaultLocLanguage] = React.useState("en");
  const [locLoading, setLocLoading] = React.useState(false);
  const [exportSelectedIds, setExportSelectedIds] = React.useState<string[]>([]);
  const [zipManifestExpanded, setZipManifestExpanded] = React.useState(false);
  const [mediaExpanded, setMediaExpanded] = React.useState(true);
  const [jsonExpanded, setJsonExpanded] = React.useState(false);
  const [csvExpanded, setCsvExpanded] = React.useState(false);
  const [localizationExpanded, setLocalizationExpanded] = React.useState(false);

  const inProgress =
    phase === "starting" || phase === "preparing" || phase === "downloading";
  const showProgress = phase !== "idle";

  React.useEffect(() => {
    if (open) {
      setScope(hasSelection ? "selection" : "all");
      setFormat(
        jsonOnly
          ? "json"
          : preferredDefault === "zip" && !zipAllowed
            ? "csv"
            : preferredDefault,
      );
      setPhase("idle");
      setError(null);
      setJobId(null);
      setModuleDetails([]);
      setLanguagePacks([]);
      setCatalogExportPreview(null);
      setCertLocByLang({});
      setDefaultLocLanguage("en");
      setExportSelectedIds([]);
      setZipManifestExpanded(false);
      setMediaExpanded(true);
      setJsonExpanded(false);
      setCsvExpanded(false);
      setLocalizationExpanded(false);
    }
  }, [open, hasSelection, preferredDefault, zipAllowed, jsonOnly]);

  const byId = React.useMemo(() => {
    const map = new Map(previewItems.map((item) => [item.id, item]));
    return map;
  }, [previewItems]);

  const rows = React.useMemo((): ExportPreviewItem[] => {
    if (scope === "selection") {
      return selectedIds.map(
        (id) => byId.get(id) ?? { id, label: id, matchKey: id },
      );
    }
    return previewItems;
  }, [scope, selectedIds, byId, previewItems]);

  const rowIds = React.useMemo(() => rows.map((r) => r.id), [rows]);
  const rowIdsKey = rowIds.join(",");

  // Default-select every item in the current scope when the candidate set changes.
  React.useEffect(() => {
    if (!open) return;
    setExportSelectedIds(rowIds);
  }, [open, rowIdsKey, rowIds]);

  const exportSelectedSet = React.useMemo(
    () => new Set(exportSelectedIds),
    [exportSelectedIds],
  );

  const selectedModuleDetails = React.useMemo(
    () => moduleDetails.filter((m) => exportSelectedSet.has(m.id)),
    [moduleDetails, exportSelectedSet],
  );

  const selectedLanguagePacks = React.useMemo(
    () =>
      languagePacks.filter((pack) => {
        const lang = previewItems.find((p) => p.matchKey === pack.code);
        return lang ? exportSelectedSet.has(lang.id) : false;
      }),
    [languagePacks, previewItems, exportSelectedSet],
  );

  const allInScopeSelected =
    rowIds.length > 0 &&
    exportSelectedIds.length === rowIds.length &&
    rowIds.every((id) => exportSelectedSet.has(id));

  // Load module details for certs (media gallery + JSON/CSV format previews).
  React.useEffect(() => {
    if (!open || !certZipFeatures || !token || !rowIdsKey) {
      if (!open || entity !== "languages") {
        setModuleDetails([]);
        if (!open) setDetailsLoading(false);
      }
      return;
    }
    let cancelled = false;
    setDetailsLoading(true);
    void (async () => {
      const details = await Promise.all(
        rowIdsKey.split(",").map((id) =>
          fetchCertificationModuleAdmin(token, id).catch(() => null),
        ),
      );
      if (cancelled) return;
      setModuleDetails(
        details.filter((d): d is CertificationModuleDetail => d != null),
      );
      setDetailsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, certZipFeatures, token, rowIdsKey, entity]);

  // Load full language packs (metadata + definitions) for JSON preview.
  React.useEffect(() => {
    if (!open || entity !== "languages" || !token || !rowIdsKey) {
      if (entity !== "languages") setLanguagePacks([]);
      return;
    }
    let cancelled = false;
    setDetailsLoading(true);
    void (async () => {
      try {
        const langs = await fetchAdminLanguages(token);
        const scoped = langs.filter((l) => rowIdsKey.split(",").includes(l.id));
        const packs = await Promise.all(
          scoped.map(async (lang: Language): Promise<LanguageExportPreview> => {
            const modules = await fetchModulesForLanguage(token, lang.code).catch(
              () => [],
            );
            return {
              code: lang.code,
              name: lang.name,
              description: lang.description ?? null,
              enabled: lang.enabled,
              isDefault: lang.isDefault,
              flagUrl: lang.flagUrl ?? null,
              flagMediaKey: null,
              modules: LANGUAGE_CONTEXTS.map((ctx) => ({
                context: ctx,
                definitions: {
                  ...(modules.find((m) => m.context === ctx)?.definitions ?? {}),
                },
              })),
            };
          }),
        );
        if (!cancelled) setLanguagePacks(packs);
      } catch {
        if (!cancelled) setLanguagePacks([]);
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, entity, token, rowIdsKey]);

  // Load deep catalog export payload for JSON preview (keys, alerts, enum groups).
  React.useEffect(() => {
    if (!open || entity !== "data-catalogs" || !token || !rowIdsKey) {
      if (entity !== "data-catalogs") setCatalogExportPreview(null);
      return;
    }
    let cancelled = false;
    setDetailsLoading(true);
    void (async () => {
      try {
        const ids = rowIdsKey.split(",").filter(Boolean);
        const { jobId } = await startExport(token, {
          entity: "data-catalogs",
          format: "json",
          scope: { ids },
        });
        await waitForJob(token, jobId);
        const { blob } = await downloadDataJob(token, jobId);
        const parsed = JSON.parse(await blob.text()) as CatalogExportPreview;
        if (!cancelled) setCatalogExportPreview(parsed);
      } catch {
        if (!cancelled) setCatalogExportPreview(null);
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, entity, token, rowIdsKey]);

  // Load certification localization definitions once for sidecar preview.
  React.useEffect(() => {
    if (!open || !certZipFeatures || !token) {
      setCertLocByLang({});
      setLocLoading(false);
      return;
    }
    let cancelled = false;
    setLocLoading(true);
    void (async () => {
      try {
        const langs = await fetchAdminLanguages(token);
        const enabled = langs.filter((l) => l.enabled);
        const defaultLanguage =
          enabled.find((l) => l.isDefault)?.code ??
          enabled.find((l) => l.code === "en")?.code ??
          enabled[0]?.code ??
          "en";
        const pairs = await Promise.all(
          enabled.map(async (lang) => {
            try {
              const mod = await fetchLocalizationModule(
                "certification",
                lang.code,
              );
              return [lang.code, mod.definitions ?? {}] as const;
            } catch {
              return [lang.code, {}] as const;
            }
          }),
        );
        if (cancelled) return;
        setDefaultLocLanguage(defaultLanguage);
        setCertLocByLang(Object.fromEntries(pairs));
      } catch {
        if (!cancelled) {
          setCertLocByLang({});
        }
      } finally {
        if (!cancelled) setLocLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, certZipFeatures, token]);

  const localizationPreview = React.useMemo(() => {
    if (entity !== "certification-modules") return null;
    const keys = collectContentKeysFromModules(selectedModuleDetails);
    return sliceLocalizationSidecar(keys, defaultLocLanguage, certLocByLang);
  }, [entity, selectedModuleDetails, defaultLocLanguage, certLocByLang]);

  const localizationSubtitle = React.useMemo(() => {
    if (!localizationPreview) return undefined;
    const langs = Object.keys(localizationPreview.definitionsByLang).length;
    const keys = collectContentKeysFromModules(selectedModuleDetails).length;
    if (!keys) return undefined;
    return `${langs} language${langs === 1 ? "" : "s"} · ${keys} key${keys === 1 ? "" : "s"}`;
  }, [localizationPreview, selectedModuleDetails]);

  const zipMedia = React.useMemo(() => {
    if (format !== "zip") return [];
    if (entity === "languages") {
      return mediaEntriesFromLanguagePacks(selectedLanguagePacks);
    }
    return mediaEntriesFromModules(selectedModuleDetails);
  }, [format, entity, selectedLanguagePacks, selectedModuleDetails]);

  const formatJsonPreview = React.useMemo(() => {
    if (entity === "certification-modules") {
      return buildExportEnvelopePreview(
        entity,
        modulesToExportItems(selectedModuleDetails),
        localizationPreview,
      );
    }
    if (entity === "languages") {
      return buildExportEnvelopePreview(entity, selectedLanguagePacks);
    }
    if (entity === "data-catalogs") {
      const selectedKeys = new Set(
        rows
          .filter((r) => exportSelectedSet.has(r.id))
          .map((r) => (r.matchKey ?? r.label).toLowerCase()),
      );
      const allItems = catalogExportPreview?.items ?? [];
      const items = allItems.filter((item) => {
        const key = String(item.slug ?? item.name ?? "").toLowerCase();
        return selectedKeys.has(key);
      });
      const referencedGroupNames = new Set<string>();
      for (const item of items) {
        for (const key of (item.keys ?? []) as Array<{ enumGroupName?: string | null }>) {
          if (key.enumGroupName) referencedGroupNames.add(key.enumGroupName);
        }
      }
      const enumGroups = (catalogExportPreview?.enumGroups ?? []).filter(
        (g) =>
          referencedGroupNames.size === 0 ||
          referencedGroupNames.has(
            String((g as { name?: string }).name ?? ""),
          ),
      );
      return buildExportEnvelopePreview(entity, items, null, {
        enumGroups,
        exportedAt:
          catalogExportPreview?.exportedAt ?? new Date().toISOString(),
      });
    }
    return buildExportEnvelopePreview(
      entity,
      rows
        .filter((r) => exportSelectedSet.has(r.id))
        .map((r) => ({
          id: r.id,
          label: r.label,
          matchKey: r.matchKey ?? null,
          imageUrl: r.imageUrl ?? null,
        })),
    );
  }, [
    entity,
    selectedModuleDetails,
    selectedLanguagePacks,
    rows,
    exportSelectedSet,
    localizationPreview,
    catalogExportPreview,
  ]);

  const showLocalizationSection =
    (format === "json" || format === "zip") && localizationPreview != null;
  const csvRows = React.useMemo((): CsvPreviewRow[] => {
    if (entity === "certification-modules") {
      return selectedModuleDetails.map((m) => ({
        id: m.id,
        slug: m.slug,
        title: m.title,
        description: m.description,
        sortOrder: m.sortOrder,
        isActive: m.isActive,
        trainingCount: m.trainings?.length ?? 0,
      }));
    }
    return rows
      .filter((r) => exportSelectedSet.has(r.id))
      .map((r) => ({
        id: r.id,
        label: r.label,
        matchKey: r.matchKey,
      }));
  }, [entity, selectedModuleDetails, rows, exportSelectedSet]);

  const csvColumns =
    entity === "certification-modules" ? certCsvColumns : genericCsvColumns;

  const noopMediaChange = React.useCallback((_items: MediaEntry[]) => {}, []);
  const noopUpload = React.useCallback(async () => ({ url: "" }), []);

  const fetchList = React.useCallback(
    (params: ListParams): Promise<ListResult<ExportPreviewItem>> => {
      const filtered = params.search
        ? rows.filter((row) =>
            [row.label, row.matchKey ?? ""].some((v) =>
              v.toLowerCase().includes(params.search!.toLowerCase()),
            ),
          )
        : rows;
      const start = (params.page - 1) * params.limit;
      return Promise.resolve({
        data: filtered.slice(start, start + params.limit),
        total: filtered.length,
      });
    },
    [rows],
  );

  const fetchCsvList = React.useCallback(
    (params: ListParams): Promise<ListResult<CsvPreviewRow>> => {
      const filtered = params.search
        ? csvRows.filter((row) =>
            Object.values(row).some((v) =>
              String(v ?? "")
                .toLowerCase()
                .includes(params.search!.toLowerCase()),
            ),
          )
        : csvRows;
      const start = (params.page - 1) * params.limit;
      return Promise.resolve({
        data: filtered.slice(start, start + params.limit),
        total: filtered.length,
      });
    },
    [csvRows],
  );

  const onExport = async () => {
    if (!token || inProgress || exportSelectedIds.length === 0) return;
    setPhase("starting");
    setError(null);
    setJobId(null);
    try {
      // Keep server-side "all" when the user hasn't trimmed the ALL scope set
      // (important when the preview list is only a loaded page of a larger total).
      const exportScope =
        scope === "all" && allInScopeSelected
          ? { all: true as const, search: search || undefined }
          : { ids: exportSelectedIds };

      const { jobId: id } = await startExport(token, {
        entity,
        format,
        scope: exportScope,
      });
      setJobId(id);
      setPhase("preparing");
      const job = await waitForJob(token, id);
      if (job.status === "failed") {
        setError(job.error || "Export failed");
        setPhase("error");
        return;
      }
      setPhase("downloading");
      const { blob, filename: headerFilename } = await downloadDataJob(token, id);
      const filename =
        headerFilename ||
        job.artifactFilename ||
        `${entity}-${Date.now()}.${format === "zip" ? "zip" : format}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      setPhase("done");
    } catch (e: any) {
      setError(e?.message || "Export failed");
      setPhase("error");
    }
  };

  const copy = progressCopy(phase, error);
  const exportCount =
    scope === "all" && allInScopeSelected
      ? totalCount
      : exportSelectedIds.length;
  const previewIncomplete =
    scope === "all" && previewItems.length > 0 && previewItems.length < totalCount;

  const formatTabs = React.useMemo(() => {
    if (jsonOnly) {
      return [{ value: "json", label: "JSON" }];
    }
    const tabs = [
      { value: "csv", label: "CSV" },
      { value: "json", label: "JSON" },
    ];
    if (zipAllowed) {
      tabs.push({ value: "zip", label: "ZIP - Includes Media" });
    }
    return tabs;
  }, [zipAllowed, jsonOnly]);

  const exportSummaryText = (() => {
    let msg = `${exportCount} ${entityLabel.toLowerCase()} will be exported`;
    if (rowIds.length > 0) {
      msg += ` · ${exportSelectedIds.length} of ${rowIds.length} selected in preview`;
    }
    if (entity === "languages" && !detailsLoading) {
      const defs = countLanguageDefinitions(selectedLanguagePacks);
      msg += ` · ${defs.toLocaleString()} definition${defs === 1 ? "" : "s"}`;
    }
    if (previewIncomplete && allInScopeSelected) {
      msg += ` (preview shows ${previewItems.length} loaded)`;
    }
    return `${msg}.`;
  })();

  return (
    <Modal
      open={open}
      setOpen={(v) => {
        if (!v) onClose();
      }}
      minWidth="720px"
      maxWidth="960px"
      maxHeight="90vh"
      closeOnClickOff={!inProgress}
    >
      <Modal.Header>
        <Text level="title-small">Export {entityLabel}</Text>
      </Modal.Header>
      <Modal.Body>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            minHeight: 320,
          }}
        >
          {!showProgress || !copy ? (
            <>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <TabSwitcher
                  aria-label={t("pro.dataTransfer.exportScope")}
                  value={scope}
                  onChange={(v) => setScope(v as "selection" | "all")}
                  tabs={[
                    {
                      value: "selection",
                      label: `Selected ${entityLabel} (${selectedIds.length})`,
                    },
                    {
                      value: "all",
                      label: `ALL ${entityLabel} (${totalCount})`,
                    },
                  ]}
                />
              </div>

              <div style={{ flex: 1, minHeight: 240 }}>
                <ListView<ExportPreviewItem>
                  listId={`export-preview-${entity}`}
                  columns={previewColumns}
                  fetchList={fetchList}
                  enabledViews={["stack"]}
                  compact
                  renderStackCard={(row, ctx) => (
                    <ListViewCard
                      id={row.id}
                      layout="stack"
                      imageFormat="cover"
                      compact={ctx.compact}
                      image={
                        row.imageUrl ? (
                          <ImageLoader src={row.imageUrl} />
                        ) : (
                          <ImageIcon size={20} style={{ opacity: 0.55 }} />
                        )
                      }
                      title={row.label}
                      subtitle={row.matchKey}
                      variant="static"
                    />
                  )}
                  enableSelection
                  defaultSelectedIds={rowIds}
                  onSelectionChange={setExportSelectedIds}
                  disableLocalPersistence
                  contextKey={`${entity}:${scope}:${rowIdsKey}`}
                  queryOptions={{ staleTime: Infinity }}
                  emptyTitle="Nothing to export"
                  emptySubtitle={
                    scope === "selection"
                      ? `Select ${entityLabel.toLowerCase()} in the list, or switch to ALL.`
                      : `No ${entityLabel.toLowerCase()} available.`
                  }
                  searchPlaceholder={`Search ${entityLabel.toLowerCase()}…`}
                  defaultPageSize={10}
                />
                <Callout variant="info">{exportSummaryText}</Callout>
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>
                <TabSwitcher
                  aria-label={t("pro.dataTransfer.exportFormat")}
                  value={format}
                  onChange={(v) => setFormat(v as "csv" | "json" | "zip")}
                  tabs={formatTabs}
                />
              </div>

              {format === "csv" && (
                <CollapsibleSection
                  id="export-preview-csv"
                  title={t("pro.dataTransfer.csvPreview")}
                  icon={<Table2 size={16} />}
                  expanded={csvExpanded}
                  onToggleExpanded={() => setCsvExpanded((v) => !v)}
                  compactHeader
                >
                  {detailsLoading && zipAllowed ? (
                    <Text level="body-small" color="muted">
                      Loading CSV preview…
                    </Text>
                  ) : (
                    <div style={{ minHeight: 160 }}>
                      <ListView<CsvPreviewRow>
                        listId={`export-csv-preview-${entity}`}
                        columns={csvColumns}
                        fetchList={fetchCsvList}
                        enabledViews={["table"]}
                        enableSelection={false}
                        disableLocalPersistence
                        contextKey={`csv:${entity}:${csvRows.map((r) => r.id).join(",")}`}
                        queryOptions={{ staleTime: Infinity }}
                        emptyTitle="No rows"
                        emptySubtitle={t("pro.dataTransfer.nothingToExportScope")}
                        searchPlaceholder="Search CSV columns…"
                        defaultPageSize={10}
                      />
                    </div>
                  )}
                </CollapsibleSection>
              )}

              {format === "json" && (
                <CollapsibleSection
                  id="export-preview-json"
                  title={t("pro.dataTransfer.jsonOutput")}
                  icon={<FileJson size={16} />}
                  expanded={jsonExpanded}
                  onToggleExpanded={() => setJsonExpanded((v) => !v)}
                  compactHeader
                >
                  {detailsLoading &&
                  (entity === "certification-modules" ||
                    entity === "languages" ||
                    entity === "data-catalogs") ? (
                    <Text level="body-small" color="muted">
                      Loading preview…
                    </Text>
                  ) : (
                    <>
                      <div style={jsonPreviewShellStyle}>
                        <JsonView
                          data={formatJsonPreview}
                          style={darkStyles}
                          shouldExpandNode={(level: number) => level < 2}
                        />
                      </div>
                      {(entity === "certification-modules" ||
                        entity === "languages" ||
                        entity === "data-catalogs") && (
                        <div style={{ marginTop: 10 }}>
                          <Callout variant="info">
                            {entity === "data-catalogs"
                              ? "Preview shows the deep catalog export (keys, alerts, enum groups). Download uses the same payload."
                              : "Preview approximates the export envelope. Media paths and timestamps are finalized on the real export."}
                          </Callout>
                        </div>
                      )}
                    </>
                  )}
                </CollapsibleSection>
              )}

              {format === "zip" && zipAllowed && (
                <>
                  <CollapsibleSection
                    id="export-preview-zip-manifest"
                    title={t("pro.dataTransfer.zipManifest")}
                    subtitle="manifest.json"
                    icon={<FileJson size={16} />}
                    expanded={zipManifestExpanded}
                    onToggleExpanded={() => setZipManifestExpanded((v) => !v)}
                    compactHeader
                  >
                    {detailsLoading ? (
                      <Text level="body-small" color="muted">
                        Loading preview…
                      </Text>
                    ) : (
                      <>
                        <div style={jsonPreviewShellStyle}>
                          <JsonView
                            data={formatJsonPreview}
                            style={darkStyles}
                            shouldExpandNode={(level: number) => level < 2}
                          />
                        </div>
                        <div style={{ marginTop: 10 }}>
                          <Callout variant="info">
                            Preview approximates the export envelope. Media paths
                            and timestamps are finalized on the real export.
                          </Callout>
                        </div>
                      </>
                    )}
                  </CollapsibleSection>

                  <CollapsibleSection
                    id="export-preview-zip-media"
                    title={t("pro.dataTransfer.mediaInZip")}
                    subtitle={
                      !detailsLoading && zipMedia.length > 0
                        ? `${zipMedia.length} file${zipMedia.length === 1 ? "" : "s"}`
                        : undefined
                    }
                    icon={<Images size={16} />}
                    expanded={mediaExpanded}
                    onToggleExpanded={() => setMediaExpanded((v) => !v)}
                    compactHeader
                  >
                    {detailsLoading ? (
                      <Text level="body-small" color="muted">
                        Loading media preview…
                      </Text>
                    ) : zipMedia.length === 0 ? (
                      <Text level="body-small" color="muted">
                        No media files will be included for the selected scope.
                      </Text>
                    ) : (
                      <MediaUploader
                        mediaType={entity === "languages" ? "image" : "video"}
                        items={zipMedia}
                        onChange={noopMediaChange}
                        onApply={noopMediaChange}
                        uploadFile={noopUpload}
                        maxItems={Math.max(zipMedia.length, 2)}
                        maxRows={2}
                        readOnly
                        selectable={false}
                        pasteEnabled={false}
                        renderBadge={renderMediaTypeBadge}
                      />
                    )}
                  </CollapsibleSection>
                </>
              )}

              {showLocalizationSection && localizationPreview && (
                <CollapsibleSection
                  id="export-preview-localization"
                  title={t("pro.system.localization.title")}
                  subtitle={locLoading ? "Loading…" : localizationSubtitle}
                  icon={<Globe size={16} />}
                  expanded={localizationExpanded}
                  onToggleExpanded={() => setLocalizationExpanded((v) => !v)}
                  compactHeader
                >
                  {locLoading ? (
                    <Text level="body-small" color="muted">
                      Loading localization…
                    </Text>
                  ) : (
                    <div style={jsonPreviewShellStyle}>
                      <JsonView
                        data={localizationPreview}
                        style={darkStyles}
                        shouldExpandNode={(level: number) => level < 2}
                      />
                    </div>
                  )}
                </CollapsibleSection>
              )}
            </>
          ) : (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                gap: 12,
                padding: "28px 12px 16px",
              }}
            >
              <Text
                level="title-small"
                weight="medium"
                color={phase === "error" ? "danger" : undefined}
              >
                {copy.title}
              </Text>
              <copy.Icon
                size={36}
                strokeWidth={1.75}
                style={{
                  color: copy.iconColor,
                  flexShrink: 0,
                  ...(copy.spin
                    ? { animation: "export-spin 0.9s linear infinite" }
                    : null),
                }}
              />
              {copy.subtitle && (
                <Text level="body-small" color="muted">
                  {copy.subtitle}
                </Text>
              )}
            </div>
          )}
        </div>
        {showProgress && phase === "starting" && (
          <style>{`@keyframes export-spin { to { transform: rotate(360deg); } }`}</style>
        )}
      </Modal.Body>
      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", minWidth: 28 }}>
          {showProgress && jobId ? (
            <span
              title={`Job ${jobId}`}
              aria-label={`Job ${jobId}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: vars.color.fgMuted,
                cursor: "default",
              }}
            >
              <Info size={16} strokeWidth={2} />
            </span>
          ) : null}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button variant="ghost" color="neutral" onClick={onClose}>
            {showProgress ? "Close" : "Cancel"}
          </Button>
          {phase === "error" ? (
            <Button color="primary" onClick={onExport} iconLeft={<Upload size={16} />}>
              Try again
            </Button>
          ) : phase === "done" ? null : (
            <Button
              color="primary"
              onClick={onExport}
              disabled={inProgress || exportSelectedIds.length === 0}
              iconLeft={<Upload size={16} />}
            >
              {inProgress ? "Exporting…" : `Export ${exportCount} ${entityLabel}`}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
